// Single source of truth for "is this the same place?" — used by the
// location picker (search saved locations), job intake (match a parsed
// stop, ask on ambiguity), the save path (upsertLocationFromStop), and
// the duplicate-merge tool. Keeping one matcher here prevents the old
// problem where two slightly different matchers disagreed (the preview
// said "new" while the save quietly matched an existing doc).
//
// Identity signal strength, strongest first:
//   1. Google Place ID            — canonical, exact.
//   2. Identical name + address   — exact for our purposes.
//   3. Coordinate proximity       — same physical spot regardless of spelling
//                                    (this is what collapses "Windsor" vs
//                                    "Windsor J" when both geocode to the
//                                    same point).
//   4. Identical address          — same place, different display name.
//   5. Identical name only        — ambiguous (could be a different branch).

// Normalize free-text for comparison: lowercase, collapse whitespace,
// strip trailing punctuation.
export function normLoc(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/[.,;]+$/g, "")
    .trim();
}

// Great-circle distance in meters between two {lat,lng} points.
export function metersBetween(a, b) {
  if (!a || !b || a.lat == null || a.lng == null || b.lat == null || b.lng == null) {
    return Infinity;
  }
  const R = 6371000; // earth radius, meters
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

// Two points within this many meters are treated as the SAME physical
// place. Kept deliberately tight (~a single building/parking lot) so we
// don't fold two different storefronts in the same plaza together — those
// stay separate and, if uncertain, get confirmed by a human.
export const SAME_PLACE_METERS = 60;

function lastUsedMs(l) {
  return l?.lastUsedAt?.toMillis?.() || l?.lastUsedAt || 0;
}

/**
 * Find the best matching saved location for an incoming candidate.
 *
 * @param {Array} locations - saved location docs ({id,name,address,lat,lng,placeId,customerName,lastUsedAt})
 * @param {Object} candidate - { name, address, lat, lng, placeId }
 * @returns {{ match: Object|null, confidence: 'exact'|'strong'|'weak'|null, reason: string|null }}
 *
 * Confidence guides the UX:
 *   exact  → safe to link automatically (Place ID or identical name+address).
 *   strong → very likely the same (same coordinates, or identical address);
 *            confirm before linking when the user asked to always confirm.
 *   weak   → same name only; always confirm.
 */
export function findLocationMatch(locations, candidate) {
  if (!Array.isArray(locations) || !candidate) {
    return { match: null, confidence: null, reason: null };
  }
  const nName = normLoc(candidate.name);
  const nAddr = normLoc(candidate.address);
  const pid = candidate.placeId || null;
  const coords =
    candidate.lat != null && candidate.lng != null
      ? { lat: candidate.lat, lng: candidate.lng }
      : null;

  // 1. Google Place ID.
  if (pid) {
    const byPid = locations.find((l) => l.placeId && l.placeId === pid);
    if (byPid) return { match: byPid, confidence: "exact", reason: "placeId" };
  }

  // 2. Identical normalized name + address.
  if (nName && nAddr) {
    const both = locations.find(
      (l) => normLoc(l.name) === nName && normLoc(l.address) === nAddr
    );
    if (both) return { match: both, confidence: "exact", reason: "name+address" };
  }

  // 3. Coordinate proximity — nearest within the threshold.
  if (coords) {
    let best = null;
    let bestD = Infinity;
    for (const l of locations) {
      if (l.lat == null || l.lng == null) continue;
      const d = metersBetween(coords, { lat: l.lat, lng: l.lng });
      if (d < bestD) {
        bestD = d;
        best = l;
      }
    }
    if (best && bestD <= SAME_PLACE_METERS) {
      return { match: best, confidence: "strong", reason: `~${Math.round(bestD)}m away` };
    }
  }

  // 4. Identical normalized address.
  if (nAddr) {
    const byAddr = locations.find((l) => normLoc(l.address) === nAddr);
    if (byAddr) return { match: byAddr, confidence: "strong", reason: "same address" };
  }

  // 5. Identical normalized name only — ambiguous. Suggest the most
  //    recently used so the owner can confirm or reject.
  if (nName) {
    const byName = locations
      .filter((l) => normLoc(l.name) === nName)
      .sort((a, b) => lastUsedMs(b) - lastUsedMs(a));
    if (byName.length) {
      return { match: byName[0], confidence: "weak", reason: "same name" };
    }
  }

  return { match: null, confidence: null, reason: null };
}

/**
 * Type-ahead search over saved locations for the picker. Ranks name
 * matches above customer/address matches, tie-broken by most-recently-used.
 *
 * @param {Array} locations
 * @param {string} queryStr
 * @param {number} limit
 * @returns {Array} matching location docs, best first.
 */
export function searchLocations(locations, queryStr, limit = 6) {
  const q = normLoc(queryStr);
  if (!q || !Array.isArray(locations)) return [];
  const scored = [];
  for (const l of locations) {
    const name = normLoc(l.name);
    const addr = normLoc(l.address);
    const cust = normLoc(l.customerName);
    let score = 0;
    if (name === q) score = 100;
    else if (name.startsWith(q)) score = 80;
    else if (name.includes(q)) score = 60;
    else if (cust && cust.includes(q)) score = 40;
    else if (addr.includes(q)) score = 20;
    if (score > 0) scored.push({ l, score, used: lastUsedMs(l) });
  }
  scored.sort((a, b) => b.score - a.score || b.used - a.used);
  return scored.slice(0, limit).map((x) => x.l);
}

// ── Fuzzy comparison for the duplicate finder ──────────────────────────
// Hand-typed and parsed addresses drift: "Ste 199" vs "suite 199", "Rd" vs
// "Road", "Fink's Jewelers" vs "Finks". These helpers reduce both sides to
// a comparable form so the merge tool still spots them.

const STREET_WORDS = {
  road: "rd", street: "st", avenue: "ave", av: "ave", drive: "dr",
  boulevard: "blvd", highway: "hwy", lane: "ln", court: "ct",
  parkway: "pkwy", place: "pl", circle: "cir", terrace: "ter",
  trail: "trl", square: "sq", suite: "ste", north: "n", south: "s",
  east: "e", west: "w", northeast: "ne", northwest: "nw",
  southeast: "se", southwest: "sw",
};
const UNIT_RE = /(?:^|\s)(?:ste|unit|apt|apartment|rm|room|bldg|building|#)\s*#?\s*([a-z0-9-]+)\b/;

// Break an address into { street, unit, city, zip }, all normalized.
export function addressParts(address) {
  const raw = String(address || "")
    .toLowerCase()
    .replace(/['.]/g, "")
    .replace(/,?\s*usa\s*$/, "");
  if (!raw.trim()) return null;
  // Last 5-digit run — the first could be a 5-digit house number.
  const zip = ([...raw.matchAll(/\b(\d{5})(?:-\d{4})?\b/g)].pop() || [])[1] || "";
  let unit = "";
  const parts = raw
    .split(",")
    .map((p) => p.replace(/[^a-z0-9# -]/g, " ").replace(/\s+/g, " ").trim())
    .map((p) => p.split(" ").map((w) => STREET_WORDS[w] || w).join(" "))
    .map((p) => {
      const m = p.match(UNIT_RE);
      if (m && !unit) {
        unit = m[1];
        return p.replace(m[0], " ").replace(/\s+/g, " ").trim();
      }
      return p;
    })
    .filter(Boolean);
  return { street: parts[0] || "", unit, city: parts[1] || "", zip };
}

// Same street address (house number + street), ignoring suite/unit,
// spelling of suffixes, and punctuation. City/zip must agree when both
// sides have them.
function sameStreet(a, b) {
  if (!a || !b || !a.street || a.street !== b.street) return false;
  if (!/\d/.test(a.street)) return false; // need a house number to be sure
  if (a.zip && b.zip) return a.zip === b.zip;
  if (a.city && b.city) return a.city === b.city;
  return true;
}

const NAME_NOISE = new Set(["the", "inc", "llc", "co", "corp", "company", "ltd", "store", "and"]);

function nameTokens(name) {
  return String(name || "")
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .split(" ")
    .filter((t) => t && !NAME_NOISE.has(t));
}

function editDistance(a, b) {
  const row = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    let prev = row[0];
    row[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const tmp = row[j];
      row[j] = Math.min(row[j] + 1, row[j - 1] + 1, prev + (a[i - 1] === b[j - 1] ? 0 : 1));
      prev = tmp;
    }
  }
  return row[b.length];
}

// "Fink's Jewelers" ~ "Finks" (one name's words all appear in the other),
// "Windsor Jewelry" ~ "Windsor Jewlery" (small typo).
export function similarNames(a, b) {
  const ta = nameTokens(a);
  const tb = nameTokens(b);
  if (!ta.length || !tb.length) return false;
  const [short, long] = ta.length <= tb.length ? [ta, tb] : [tb, ta];
  const longSet = new Set(long);
  if (short.every((t) => longSet.has(t))) return true;
  const ja = ta.join(" ");
  const jb = tb.join(" ");
  const maxLen = Math.max(ja.length, jb.length);
  return maxLen >= 5 && editDistance(ja, jb) / maxLen <= 0.15;
}

/**
 * Find clusters of likely-duplicate saved locations, for the merge tool.
 * Two docs are flagged when they'd match each other at 'exact' or 'strong'
 * confidence (same Place ID, identical name+address, same coordinates, or
 * identical address), or fuzzily: the same street address once suite and
 * suffix spellings are normalized — with the same unit, or with similar
 * names when the units differ or one is missing. Name-only matches are
 * NOT auto-flagged here (could be another branch of the same business).
 *
 * @param {Array} locations
 * @returns {Array<Array>} groups of 2+ location docs that look like dupes.
 */
export function findDuplicateGroups(locations) {
  if (!Array.isArray(locations)) return [];
  const parent = new Map(); // union-find
  const find = (id) => {
    while (parent.get(id) !== id) {
      parent.set(id, parent.get(parent.get(id)));
      id = parent.get(id);
    }
    return id;
  };
  const union = (a, b) => {
    parent.set(find(a), find(b));
  };
  locations.forEach((l) => parent.set(l.id, l.id));
  const addrs = new Map(locations.map((l) => [l.id, addressParts(l.address)]));

  for (let i = 0; i < locations.length; i++) {
    for (let j = i + 1; j < locations.length; j++) {
      const a = locations[i];
      const b = locations[j];
      // Owner said "these are different places" (Not-duplicates button in
      // the merge tool writes notSameAs to both docs) — never link this
      // pair again. A NEW location matching either one still gets flagged.
      if ((a.notSameAs || []).includes(b.id) || (b.notSameAs || []).includes(a.id)) continue;
      const samePid = a.placeId && b.placeId && a.placeId === b.placeId;
      const sameNameAddr =
        normLoc(a.name) === normLoc(b.name) && normLoc(a.address) === normLoc(b.address);
      const sameAddr =
        normLoc(a.address) && normLoc(a.address) === normLoc(b.address);
      const close =
        a.lat != null && b.lat != null &&
        metersBetween(a, b) <= SAME_PLACE_METERS;
      const pa = addrs.get(a.id);
      const pb = addrs.get(b.id);
      const fuzzyAddr = sameStreet(pa, pb) &&
        (pa.unit === pb.unit || similarNames(a.name, b.name));
      if (samePid || sameNameAddr || sameAddr || close || fuzzyAddr) union(a.id, b.id);
    }
  }

  const groups = new Map();
  for (const l of locations) {
    const root = find(l.id);
    if (!groups.has(root)) groups.set(root, []);
    groups.get(root).push(l);
  }
  return [...groups.values()].filter((g) => g.length > 1);
}
